import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { 
  UNLIMITED_SEATS, 
  UNLIMITED_CREDITS, 
  UNLIMITED_MONTE_CARLO, 
  UNLIMITED_VALUE 
} from '@/constants/limits';

export type AccountType = 'quant' | 'quant-pro' | 'enterprise';

// Administrator/Developer override emails - these users get enterprise access regardless of their metadata
const ADMIN_EMAILS = [
  'es@papartners.co', // Evan Spencer - Lead Engineer/Creator
  // Add other admin emails here as needed
] as const;

export interface AccountTypePermissions {
  // Core features
  seats: number;
  aumCapacity: string;
  creditsIncluded: number;
  additionalSeatsAllowed?: boolean;
  
  // Strategy & Models
  availableModels: string[];
  maxStrategies?: number;
  
  // Analysis & Backtesting  
  backtestingMode: 'standard' | 'enhanced' | 'unlimited';
  monteCarloLimit: number;
  
  // Compliance & Features
  hasTaxEfficiency: boolean;
  taxEfficiencyUsageLimit?: number; // Monthly usage limit for tax efficiency
  hasCSVUpload: boolean;
  hasAdvancedCompliance: boolean;
  hasCommunicationsMonitor: boolean;
  hasDisclosureManagement: boolean;
  
  // Portfolio & Trading
  portfolioLevel: 'basic' | 'advanced' | 'enterprise';
  paperTradingLevel: 'basic' | 'full' | 'enterprise';
  
  // Support & Access
  supportLevel: 'standard' | 'priority' | 'dedicated';
  apiAccessLevel: 'limited' | 'standard' | 'full';
  
  // Chat features
  chatHistoryLevel: 'standard' | 'enhanced' | 'unlimited';
  monthlyAnalysisLimit?: number;
}

const ACCOUNT_TYPE_PERMISSIONS: Record<AccountType, AccountTypePermissions> = {
  quant: {
    seats: 1,
    aumCapacity: '$1M',
    creditsIncluded: 1000,
    additionalSeatsAllowed: false,
    availableModels: ['arima', 'xgboost'],
    backtestingMode: 'standard',
    monteCarloLimit: 3,
    hasTaxEfficiency: false,
    hasCSVUpload: false,
    hasAdvancedCompliance: false,
    hasCommunicationsMonitor: false,
    hasDisclosureManagement: false,
    portfolioLevel: 'basic',
    paperTradingLevel: 'basic',
    supportLevel: 'standard',
    apiAccessLevel: 'limited',
    chatHistoryLevel: 'standard',
    monthlyAnalysisLimit: 5
  },
  'quant-pro': {
    seats: 4,
    aumCapacity: '$10M',
    creditsIncluded: 6000,
    additionalSeatsAllowed: true,
    availableModels: ['arima', 'xgboost', 'stewie_quant', 'lstm'],
    backtestingMode: 'enhanced',
    monteCarloLimit: 25,
    hasTaxEfficiency: true,
    taxEfficiencyUsageLimit: 50, // Limited monthly tax efficiency operations
    hasCSVUpload: true,
    hasAdvancedCompliance: true,
    hasCommunicationsMonitor: true,
    hasDisclosureManagement: true,
    portfolioLevel: 'advanced',
    paperTradingLevel: 'full',
    supportLevel: 'priority',
    apiAccessLevel: 'standard',
    chatHistoryLevel: 'enhanced',
    monthlyAnalysisLimit: 25 // More than quant (5), less than enterprise (unlimited)
  },
  enterprise: {
    seats: UNLIMITED_SEATS,
    aumCapacity: 'Unlimited',
    creditsIncluded: UNLIMITED_CREDITS,
    additionalSeatsAllowed: true,
    availableModels: ['arima', 'xgboost', 'stewie_quant', 'lstm', 'hdk', 'rl_dqn'],
    backtestingMode: 'unlimited',
    monteCarloLimit: UNLIMITED_MONTE_CARLO,
    hasTaxEfficiency: true,
    hasCSVUpload: true,
    hasAdvancedCompliance: true,
    hasCommunicationsMonitor: true,
    hasDisclosureManagement: true,
    portfolioLevel: 'enterprise',
    paperTradingLevel: 'enterprise',
    supportLevel: 'dedicated',
    apiAccessLevel: 'full',
    chatHistoryLevel: 'unlimited'
  }
};

export const useAccountType = () => {
  const [accountType, setAccountType] = useState<AccountType | null>(null);
  const [permissions, setPermissions] = useState<AccountTypePermissions | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Add a small delay to prevent conflicts with ProtectedRoute auth check
    const timer = setTimeout(() => {
      fetchAccountType();
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  const fetchAccountType = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Use a more robust auth check with timeout for development
      const isDevelopment = () => {
        const hostname = window.location.hostname;
        const port = window.location.port;
        const protocol = window.location.protocol;
        
        console.log('useAccountType: Environment check', { 
          hostname, 
          port, 
          protocol, 
          href: window.location.href 
        });
        
        return hostname === 'localhost' || 
               hostname === '127.0.0.1' ||
               port === '5173' ||
               port === '3000' ||
               hostname.includes('localhost') ||
               (protocol === 'http:' && (hostname === 'localhost' || hostname === '127.0.0.1'));
      };
      
      const isDevEnv = isDevelopment();
      
      let user = null;
      
      if (isDevEnv) {
        // In development, try auth with timeout and fallback
        try {
          const authPromise = supabase.auth.getUser();
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Auth timeout')), 3000)
          );
          
          const { data: { user: authUser } } = await Promise.race([authPromise, timeoutPromise]) as any;
          user = authUser;
        } catch (authError) {
          console.warn('useAccountType: Auth check timed out or failed, using fallback');
          // For development, assume enterprise access if we can't get user info
          setAccountType('enterprise');
          setPermissions(ACCOUNT_TYPE_PERMISSIONS.enterprise);
          setIsLoading(false);
          return;
        }
      } else {
        // Production - normal auth check
        const { data: { user: authUser } } = await supabase.auth.getUser();
        user = authUser;
      }

      if (!user) {
        // If no user found, don't throw error - just set default
        console.warn('useAccountType: No user found, setting default quant access');
        setAccountType('quant');
        setPermissions(ACCOUNT_TYPE_PERMISSIONS.quant);
        setIsLoading(false);
        return;
      }

      // Check if user is an administrator/developer - these get enterprise access automatically
      const userEmail = user.email?.toLowerCase();
      const isAdmin = userEmail && ADMIN_EMAILS.includes(userEmail as any);
      
      if (isAdmin) {
        console.log(`Administrator access granted for ${userEmail}`);
        setAccountType('enterprise');
        setPermissions(ACCOUNT_TYPE_PERMISSIONS.enterprise);
        setIsLoading(false);
        return;
      }

      // Get user metadata which should contain account type from signup
      const userMetadata = user.user_metadata;
      const savedAccountType = userMetadata?.user_type;

      // Validate account type before using it
      const isValidAccountType = (type: any): type is AccountType => {
        return ['quant', 'quant-pro', 'enterprise'].includes(type);
      };

      if (isValidAccountType(savedAccountType) && ACCOUNT_TYPE_PERMISSIONS[savedAccountType]) {
        setAccountType(savedAccountType);
        setPermissions(ACCOUNT_TYPE_PERMISSIONS[savedAccountType]);
      } else {
        // Fallback to quant if no account type found
        console.warn('No account type found in user metadata, defaulting to quant');
        setAccountType('quant');
        setPermissions(ACCOUNT_TYPE_PERMISSIONS.quant);
      }
    } catch (error) {
      console.error('Error fetching account type:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch account type');
      
      // Fallback to quant on error - don't let this break the app
      setAccountType('quant');
      setPermissions(ACCOUNT_TYPE_PERMISSIONS.quant);
    } finally {
      setIsLoading(false);
    }
  };

  const hasModelAccess = (modelId: string): boolean => {
    if (!permissions) return false;
    return permissions.availableModels.includes(modelId);
  };

  const canUploadCSV = (): boolean => {
    return permissions?.hasCSVUpload ?? false;
  };

  const canAccessTaxEfficiency = (): boolean => {
    return permissions?.hasTaxEfficiency ?? false;
  };

  const getAvailableModels = () => {
    return permissions?.availableModels ?? [];
  };

  const canRunMonteCarloBacktest = (currentCount: number): boolean => {
    if (!permissions) return false;
    return currentCount < permissions.monteCarloLimit;
  };

  const getRemainingAnalysis = (usedCount: number): number => {
    const limit = permissions?.monthlyAnalysisLimit;
    if (limit == null) {
      return UNLIMITED_VALUE;
    }
    return Math.max(0, limit - usedCount);
  };

  const canAccessCommunicationsMonitor = (): boolean => {
    return permissions?.hasCommunicationsMonitor ?? false;
  };

  const canAccessDisclosureManagement = (): boolean => {
    return permissions?.hasDisclosureManagement ?? false;
  };

  const canAddAdditionalSeats = (): boolean => {
    return permissions?.additionalSeatsAllowed ?? false;
  };

  const getTaxEfficiencyUsageLimit = (): number => {
    return permissions?.taxEfficiencyUsageLimit ?? 0;
  };

  const getPortfolioLevel = (): string => {
    return permissions?.portfolioLevel ?? 'basic';
  };

  const getPaperTradingLevel = (): string => {
    return permissions?.paperTradingLevel ?? 'basic';
  };

  const isAdministrator = async (): Promise<boolean> => {
    // This can be called independently to check admin status
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const userEmail = user?.email?.toLowerCase();
      return accountType === 'enterprise' && userEmail ? ADMIN_EMAILS.includes(userEmail as any) : false;
    } catch {
      return false;
    }
  };

  return {
    accountType,
    permissions,
    isLoading,
    error,
    hasModelAccess,
    canUploadCSV,
    canAccessTaxEfficiency,
    canAccessCommunicationsMonitor,
    canAccessDisclosureManagement,
    canAddAdditionalSeats,
    getAvailableModels,
    canRunMonteCarloBacktest,
    getRemainingAnalysis,
    getTaxEfficiencyUsageLimit,
    getPortfolioLevel,
    getPaperTradingLevel,
    isAdministrator,
    refetch: fetchAccountType
  };
}; 